/**
 * 🚀 V170.1: Declined Request Cache System
 * Client-side cache to prevent duplicate connection request errors
 * 
 * Problem: Database throws "duplicate key value violates unique constraint" 
 * when trying to INSERT a request that was previously declined.
 * 
 * Solution: Cache declined requests in localStorage with 7-day TTL.
 * Check cache BEFORE attempting database INSERT to prevent errors.
 * 
 * Features:
 * - ✅ Instant cache checks (no server round-trip)
 * - ✅ localStorage persistence (survives page reloads)
 * - ✅ 7-day TTL with automatic expiration
 * - ✅ Auto-cleanup of expired entries
 * - ✅ Quota management (handles storage limits)
 * - ✅ Comprehensive error handling
 * - ✅ Statistics API for monitoring
 */

// ============================================================================
// TYPES
// ============================================================================

export interface DeclinedRequestEntry {
  senderId: string;
  recipientId: string;
  recipientName?: string;
  declinedAt: string; // ISO timestamp
  expiresAt: number; // Unix timestamp (milliseconds)
}

export interface DeclinedCacheStats {
  totalEntries: number;
  activeEntries: number;
  expiredEntries: number;
  oldestEntry?: string;
  newestEntry?: string;
  cacheSize: number; // bytes
}

interface DeclinedCacheStorage {
  version: string;
  entries: DeclinedRequestEntry[];
  lastCleanup: number; // Unix timestamp
}

// ============================================================================
// CONSTANTS
// ============================================================================

const CACHE_VERSION = '1.0.0';
const STORAGE_KEY = 'blissnet_declined_requests_v170';
const DEFAULT_TTL_DAYS = 7;
const DEFAULT_TTL_MS = DEFAULT_TTL_DAYS * 24 * 60 * 60 * 1000;
const MAX_ENTRIES = 1000; // Prevent localStorage bloat
const CLEANUP_INTERVAL_MS = 60 * 60 * 1000; // Cleanup every hour

// ============================================================================
// CACHE CLASS
// ============================================================================

class DeclinedRequestCache {
  private cache: DeclinedCacheStorage | null = null;
  private cleanupTimer: number | null = null;

  constructor() {
    this.initialize();
  }

  /**
   * Initialize cache from localStorage
   */
  private initialize(): void {
    try {
      if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
        this.cache = this.createEmptyCache();
        return;
      }

      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        this.cache = this.createEmptyCache();
        this.saveToStorage();
        return;
      }

      const parsed = JSON.parse(stored) as DeclinedCacheStorage;

      // Version check - clear cache if version mismatch
      if (parsed.version !== CACHE_VERSION) {
        this.cache = this.createEmptyCache();
        this.saveToStorage();
        return;
      }

      this.cache = parsed;

      // Run cleanup if needed
      const timeSinceCleanup = Date.now() - parsed.lastCleanup;
      if (timeSinceCleanup > CLEANUP_INTERVAL_MS) {
        this.cleanupExpiredEntries();
      }

      // Setup periodic cleanup
      this.setupPeriodicCleanup();
    } catch (error) {
      console.error('❌ V170.1: Error initializing declined request cache:', error);
      this.cache = this.createEmptyCache();
    }
  }

  /**
   * Create empty cache structure
   */
  private createEmptyCache(): DeclinedCacheStorage {
    return {
      version: CACHE_VERSION,
      entries: [],
      lastCleanup: Date.now()
    };
  }

  /**
   * Save cache to localStorage
   */
  private saveToStorage(): void {
    if (!this.cache) return;

    try {
      if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
        return;
      }

      const serialized = JSON.stringify(this.cache);
      const size = new Blob([serialized]).size;

      // Check storage quota
      try {
        localStorage.setItem(STORAGE_KEY, serialized);
      } catch (quotaError: any) {
        // Quota exceeded - remove oldest entries
        if (quotaError.name === 'QuotaExceededError' || quotaError.code === 22) {
          this.removeOldestEntries(Math.floor(this.cache.entries.length * 0.2)); // Remove 20%
          const retrySerialized = JSON.stringify(this.cache);
          localStorage.setItem(STORAGE_KEY, retrySerialized);
        } else {
          throw quotaError;
        }
      }
    } catch (error) {
      console.error('❌ V170.1: Error saving declined request cache:', error);
      // Continue with memory-only cache
    }
  }

  /**
   * Normalize cache key (ensure consistent ordering)
   */
  private normalizeKey(senderId: string, recipientId: string): string {
    // Sort IDs lexicographically to ensure consistent key regardless of direction
    const [id1, id2] = [senderId, recipientId].sort();
    return `${id1}-${id2}`;
  }

  /**
   * Check if request was declined (instant check)
   */
  isRequestDeclined(senderId: string, recipientId: string): DeclinedRequestEntry | null {
    if (!this.cache || !senderId || !recipientId) {
      return null;
    }

    try {
      const key = this.normalizeKey(senderId, recipientId);
      const now = Date.now();

      // Find matching entry (check both directions)
      const entry = this.cache.entries.find(
        e => this.normalizeKey(e.senderId, e.recipientId) === key
      );

      if (!entry) {
        return null;
      }

      // Check expiration
      if (now > entry.expiresAt) {
        this.removeDeclinedRequest(senderId, recipientId);
        return null;
      }

      return entry;
    } catch (error) {
      console.error('❌ V170.1: Error checking declined request:', error);
      return null;
    }
  }

  /**
   * Add declined request to cache
   */
  addDeclinedRequest(
    senderId: string,
    recipientId: string,
    recipientName?: string,
    declinedAt?: string
  ): void {
    if (!senderId || !recipientId) {
      return;
    }

    if (!this.cache) {
      this.cache = this.createEmptyCache();
    }

    try {
      const now = Date.now();
      const declinedTimestamp = declinedAt ? new Date(declinedAt).getTime() : now;
      const expiresAt = declinedTimestamp + DEFAULT_TTL_MS;

      const entry: DeclinedRequestEntry = {
        senderId,
        recipientId,
        recipientName,
        declinedAt: declinedAt || new Date().toISOString(),
        expiresAt
      };

      // Remove existing entry if present (update)
      this.removeDeclinedRequest(senderId, recipientId);

      // Check max entries limit
      if (this.cache.entries.length >= MAX_ENTRIES) {
        this.removeOldestEntries(1);
      }

      // Add new entry
      this.cache.entries.push(entry);

      this.saveToStorage();
    } catch (error) {
      console.error('❌ V170.1: Error adding declined request:', error);
    }
  }

  /**
   * Remove declined request from cache (when reconnecting)
   */
  removeDeclinedRequest(senderId: string, recipientId: string): void {
    if (!this.cache || !senderId || !recipientId) {
      return;
    }

    try {
      const key = this.normalizeKey(senderId, recipientId);
      const initialLength = this.cache.entries.length;

      this.cache.entries = this.cache.entries.filter(
        e => this.normalizeKey(e.senderId, e.recipientId) !== key
      );

      if (this.cache.entries.length < initialLength) {
        this.saveToStorage();
      }
    } catch (error) {
      console.error('❌ V170.1: Error removing declined request:', error);
    }
  }

  /**
   * Get all declined requests for a sender
   */
  getAllDeclinedForSender(senderId: string): DeclinedRequestEntry[] {
    if (!this.cache || !senderId) {
      return [];
    }

    try {
      const now = Date.now();
      return this.cache.entries.filter(
        e => e.senderId === senderId && now <= e.expiresAt
      );
    } catch (error) {
      console.error('🚀 V170.1: Error getting declined requests for sender:', error);
      return [];
    }
  }

  /**
   * Cleanup expired entries
   */
  private cleanupExpiredEntries(): void {
    if (!this.cache) {
      return;
    }

    try {
      const now = Date.now();
      const initialLength = this.cache.entries.length;

      this.cache.entries = this.cache.entries.filter(e => now <= e.expiresAt);

      const removed = initialLength - this.cache.entries.length;
      this.cache.lastCleanup = now;

      if (removed > 0) {
        this.saveToStorage();
      }
    } catch (error) {
      console.error('❌ V170.1: Error during cleanup:', error);
    }
  }

  /**
   * Remove oldest entries (for quota management)
   */
  private removeOldestEntries(count: number): void {
    if (!this.cache || count <= 0) {
      return;
    }

    try {
      // Sort by expiresAt (oldest first) and remove
      this.cache.entries.sort((a, b) => a.expiresAt - b.expiresAt);
      this.cache.entries.splice(0, count);
    } catch (error) {
      console.error('❌ V170.1: Error removing oldest entries:', error);
    }
  }

  /**
   * Setup periodic cleanup
   */
  private setupPeriodicCleanup(): void {
    if (this.cleanupTimer !== null) {
      return; // Already setup
    }

    if (typeof window === 'undefined') {
      return;
    }

    this.cleanupTimer = window.setInterval(() => {
      this.cleanupExpiredEntries();
    }, CLEANUP_INTERVAL_MS);

    // Cleanup on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.cleanupExpiredEntries();
        if (this.cleanupTimer !== null) {
          window.clearInterval(this.cleanupTimer);
        }
      });
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): DeclinedCacheStats {
    if (!this.cache) {
      return {
        totalEntries: 0,
        activeEntries: 0,
        expiredEntries: 0,
        cacheSize: 0
      };
    }

    try {
      const now = Date.now();
      const activeEntries = this.cache.entries.filter(e => now <= e.expiresAt);
      const expiredEntries = this.cache.entries.length - activeEntries.length;

      const sortedByDate = [...this.cache.entries].sort(
        (a, b) => new Date(a.declinedAt).getTime() - new Date(b.declinedAt).getTime()
      );

      const serialized = JSON.stringify(this.cache);
      const cacheSize = new Blob([serialized]).size;

      return {
        totalEntries: this.cache.entries.length,
        activeEntries: activeEntries.length,
        expiredEntries,
        oldestEntry: sortedByDate[0]?.declinedAt,
        newestEntry: sortedByDate[sortedByDate.length - 1]?.declinedAt,
        cacheSize
      };
    } catch (error) {
      console.error('🚀 V170.1: Error calculating stats:', error);
      return {
        totalEntries: 0,
        activeEntries: 0,
        expiredEntries: 0,
        cacheSize: 0
      };
    }
  }

  /**
   * Clear all entries (for testing/debugging)
   */
  clearAll(): void {
    this.cache = this.createEmptyCache();
    this.saveToStorage();
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

const declinedRequestCache = new DeclinedRequestCache();

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Check if request was declined (instant check)
 * @returns Entry if declined, null otherwise
 */
export function isRequestDeclined(
  senderId: string,
  recipientId: string
): DeclinedRequestEntry | null {
  return declinedRequestCache.isRequestDeclined(senderId, recipientId);
}

/**
 * Add declined request to cache
 */
export function addDeclinedRequest(
  senderId: string,
  recipientId: string,
  recipientName?: string,
  declinedAt?: string
): void {
  declinedRequestCache.addDeclinedRequest(senderId, recipientId, recipientName, declinedAt);
}

/**
 * Remove declined request from cache (when reconnecting)
 */
export function removeDeclinedRequest(senderId: string, recipientId: string): void {
  declinedRequestCache.removeDeclinedRequest(senderId, recipientId);
}

/**
 * Get all declined requests for a sender
 */
export function getAllDeclinedForSender(senderId: string): DeclinedRequestEntry[] {
  return declinedRequestCache.getAllDeclinedForSender(senderId);
}

/**
 * Get cache statistics
 */
export function getDeclinedCacheStats(): DeclinedCacheStats {
  return declinedRequestCache.getStats();
}

/**
 * Clear all cache entries (for testing/debugging)
 */
export function clearDeclinedCache(): void {
  declinedRequestCache.clearAll();
}

